import { useEffect } from 'react';
import './styles/globals.css';
import { useAuthStore } from './store/auth.store';
import { useChatStore } from './store/chat.store';
import { initHostBridge, exchangeToken } from './lib/host-bridge';
import { apiFetch, type Attachment } from './lib/api';
import { Composer } from './components/chat/Composer';
import { MessageStream } from './components/chat/MessageStream';
import Header from './components/Header';
import ConversationList from './components/chat/ConversationList';
import { QuotaBanner } from './components/chat/QuotaBanner';
import { SkillList } from './components/skills/SkillList';
import { useUiStore } from './store/ui.store';
import { useQuota } from './hooks/useQuota';
import { useStream } from './hooks/useStream';
import { SettingsSheet } from './components/settings/SettingsSheet';
import { useThemeStore } from './store/theme.store';

export function App() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const role = useAuthStore((s) => s.role);
  const isReadOnly = role === 'support_readonly';

  const surface = useUiStore((s) => s.surface);
  const setSurface = useUiStore((s) => s.setSurface);
  const streamingConversationIds = useUiStore(
    (s) => s.streamingConversationIds,
  );
  const startStreaming = useUiStore((s) => s.startStreaming);
  const stopStreaming = useUiStore((s) => s.stopStreaming);

  const historyOpen = useUiStore((s) => s.historyOpen);
  const setHistoryOpen = useUiStore((s) => s.setHistoryOpen);
  const toggleHistory = useUiStore((s) => s.toggleHistory);
  const {
    conversations,
    activeConversationId,
    setConversations,
    setActiveConversation,
    setMessages,
    addUserMessage,
    quotaExceeded,
    setQuotaExceeded,
  } = useChatStore();
  const { sendMessage, retryTurn, stopStream } = useStream();

  // Send is gated only when the conversation currently on screen is the one streaming.
  const isActiveStreaming =
    activeConversationId != null &&
    streamingConversationIds.has(activeConversationId);

  const { data: quotaData } = useQuota();

  useEffect(() => {
    // if (quotaData?.quota?.exceeded) setQuotaExceeded(true);

    // initialize theme
    useThemeStore.getState().init();
  }, [quotaData]);

  useEffect(() => {
    initHostBridge();
    const params = new URLSearchParams(location.search);

    // Dev convenience only — never honored in a production build.
    if (import.meta.env.DEV) {
      const devToken = params.get('dev_token');
      if (devToken) exchangeToken(devToken).catch(console.error);
    }

    // Impersonation: token arrives in the URL fragment so it never hits the
    // server or access logs. Strip it from the address bar before exchanging.
    const hash = new URLSearchParams(location.hash.slice(1));
    const impToken = hash.get('impersonation_token');
    if (impToken) {
      history.replaceState(null, '', location.pathname + location.search);
      exchangeToken(impToken).catch(console.error);
    }

    if (params.has('quota')) setQuotaExceeded(true);
  }, []);

  useEffect(() => {
    if (!accessToken) return;
    apiFetch('/api/chat/conversations')
      .then((r) => {
        if (!r.ok)
          throw new Error(`Failed to fetch conversations: ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (Array.isArray(d.conversations)) setConversations(d.conversations);
      })
      .catch(console.error);
  }, [accessToken]);

  async function loadConversation(id: string) {
    setMessages([]);
    setActiveConversation(id);
    setHistoryOpen(false);
    try {
      const r = await apiFetch(`/api/chat/conversations/${id}`);
      if (!r.ok) throw new Error(`Failed to load conversation: ${r.status}`);
      const d = await r.json();
      if (!Array.isArray(d.messages)) return;
      setMessages(
        d.messages
          .filter((m: any) => m.status !== 'streaming')
          .map((m: any) => ({
            id: m.id,
            role: m.role,
            content: Array.isArray(m.content) ? m.content : [],
            status: m.status,
            toolCallLabel: null,
          })),
      );
    } catch (e) {
      console.error(e);
    }
  }

  /** Returns the active conversation, creating one on first message. */
  async function ensureConversation(): Promise<string | null> {
    if (activeConversationId) return activeConversationId;
    const res = await apiFetch('/api/chat/conversations', { method: 'POST' });
    if (res.status === 429) {
      setQuotaExceeded(true);
      return null;
    }
    const { conversation } = await res.json();
    setConversations([conversation, ...conversations]);
    setActiveConversation(conversation.id);
    return conversation.id as string;
  }

  async function handleSend(
    content: string,
    attachments: Attachment[] = [],
    conversationId?: string,
  ) {
    // The Composer resolves the id before uploading (it needs one to build the
    // storage key) and passes it through. Suggested-prompt clicks arrive
    // without one, so fall back to creating it here.
    const convId = conversationId ?? (await ensureConversation());
    if (!convId) return;

    addUserMessage(
      content,
      attachments.map((a) => ({ fileId: a.fileId, name: a.name })),
    );
    startStreaming(convId);
    try {
      await sendMessage(convId, content, attachments);
    } finally {
      stopStreaming(convId);
    }
  }

  async function handleRename(id: string, title: string) {
    await apiFetch(`/api/chat/conversations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
    setConversations(
      conversations.map((c) => (c.id === id ? { ...c, title } : c)),
    );
  }

  async function handleDelete(id: string) {
    await apiFetch(`/api/chat/conversations/${id}`, { method: 'DELETE' });
    setConversations(conversations.filter((c) => c.id !== id));
    if (activeConversationId === id) {
      setActiveConversation(null);
      setMessages([]);
    }
  }

  if (!accessToken) {
    return (
      <div className="flex h-screen flex-col bg-white">
        {/* Header skeleton */}
        <div className="border-ink/8 flex h-12 items-center border-b px-4">
          <div className="bg-ink/8 h-4 w-24 animate-pulse rounded" />
        </div>
        {/* Message skeleton rows */}
        <div className="flex flex-1 flex-col gap-4 px-4 py-4">
          <div className="bg-ink/6 h-10 w-2/3 animate-pulse rounded-sm" />
          <div className="bg-ink/6 h-16 w-3/4 animate-pulse self-end rounded-sm" />
          <div className="bg-ink/6 h-10 w-1/2 animate-pulse rounded-sm" />
        </div>
        {/* Composer skeleton */}
        <div className="px-4 pt-2 pb-4">
          <div className="border-ink/10 bg-ink/4 h-12 animate-pulse rounded-sm border" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-white">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {surface === 'chat' || surface === 'settings' ? (
            <>
              <Header
                onHistoryClick={toggleHistory}
                onNewConversation={() => {
                  setActiveConversation(null);
                  setMessages([]);
                  setHistoryOpen(false);
                }}
                onChangeSurface={setSurface}
              />
              <MessageStream
                onPromptClick={handleSend}
                onRetryTurn={retryTurn}
              />

              {quotaExceeded && (
                <QuotaBanner onDismiss={() => setQuotaExceeded(false)} />
              )}

              {isReadOnly && (
                <div className="flex items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-sm font-semibold text-white">
                  <span>
                    Support view — read only. Actions and replies are disabled.
                  </span>
                </div>
              )}

              <Composer
                onSend={handleSend}
                ensureConversation={ensureConversation}
                disabled={quotaExceeded || isReadOnly}
                streaming={isActiveStreaming}
                onStop={stopStream}
              />
            </>
          ) : surface === 'skills' ? (
            role === 'admin' ? (
              <SkillList
                onBack={() => setSurface('chat')}
                onSettingsClick={() => setSurface('settings')}
              />
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
                <p className="text-lg font-semibold text-neutral-900">
                  Access denied
                </p>
                <p className="text-sm text-neutral-500">
                  You need admin access to manage skills.
                </p>
                <button
                  onClick={() => setSurface('chat')}
                  className="mt-2 rounded border border-neutral-300 px-4 py-2 text-sm font-medium hover:bg-neutral-50"
                >
                  Go back
                </button>
              </div>
            )
          ) : null}
        </div>
        <ConversationList
          conversations={conversations}
          activeConversationId={activeConversationId}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          onSelect={(id) => {
            loadConversation(id);
            setHistoryOpen(false);
          }}
          onRename={handleRename}
          onDelete={handleDelete}
          onNewConversation={() => {
            setActiveConversation(null);
            setMessages([]);
            setHistoryOpen(false);
          }}
          onChangeSurface={setSurface}
        />

        <SettingsSheet
          open={surface === 'settings'}
          onClose={() => setSurface('chat')}
        />
      </div>
    </div>
  );
}
